import { useCallback, useEffect, useMemo, useState } from 'react';
import { activateUser, deactivateUser, getUsers } from '../api/usersApi';

const mapUserRow = (user) => ({
  id: user.id,
  username: user.username,
  firstname: user.firstname,
  lastname: user.lastname,
  nombre: `${user.firstname} ${user.lastname}`,
  email: user.email,
  profesional: user.profesional,
  role: user.role,
  createdAt: user.createdAt,
  active: user.active,
});

export const useUsersAdminTable = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterId, setFilterId] = useState('');
  const [filterNombre, setFilterNombre] = useState('');
  const [filterProfesional, setFilterProfesional] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [sortDate, setSortDate] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      setRows(data.map(mapUserRow));
    } catch (error) {
      console.error('Error al obtener los usuarios:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredRows = useMemo(() => (
    rows
      .filter((row) => filterId === '' || row.id.toString().includes(filterId))
      .filter((row) => filterNombre === '' || row.nombre.toLowerCase().includes(filterNombre.toLowerCase()))
      .filter((row) => filterProfesional === '' || String(row.profesional) === filterProfesional)
      .filter((row) => filterRole === '' || row.role === filterRole)
      .filter((row) => filterActive === '' || (filterActive === 'active' ? row.active : !row.active))
      .sort((a, b) => {
        if (sortDate === 'asc') return new Date(a.createdAt) - new Date(b.createdAt);
        if (sortDate === 'desc') return new Date(b.createdAt) - new Date(a.createdAt);
        return 0;
      })
  ), [rows, filterId, filterNombre, filterProfesional, filterRole, filterActive, sortDate]);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((row) => row.active).length,
    inactive: rows.filter((row) => !row.active).length,
    admins: rows.filter((row) => row.role === 'ADMIN' || row.role === 'SUPERADMIN').length,
  }), [rows]);

  const resetFilters = useCallback(() => {
    setFilterId('');
    setFilterNombre('');
    setFilterProfesional('');
    setFilterRole('');
    setFilterActive('');
    setSortDate(null);
  }, []);

  const toggleUserActive = async (row) => {
    if (row.active) {
      await deactivateUser(row.id);
    } else {
      await activateUser(row.id);
    }
    await fetchUsers();
  };

  return {
    rows: filteredRows,
    loading,
    stats,
    filterId,
    setFilterId,
    filterNombre,
    setFilterNombre,
    filterProfesional,
    setFilterProfesional,
    filterRole,
    setFilterRole,
    filterActive,
    setFilterActive,
    sortDate,
    setSortDate,
    resetFilters,
    fetchUsers,
    toggleUserActive,
  };
};
